<script lang="ts" setup>
import type { GalleryOptions } from '~layers/composables/useUiMediaGallery.ts'

const props = withDefaults(defineProps<
  {
    name?: string
    options: GalleryOptions
  }
>(), {
  name: () => 'media-gallery',
})

const { galleryItems, galleryOptions } = useUiMediaGallery(props.name, props.options)

const { close } = useUiDialog(props.name)

onUnmounted(() => {
  close()
})
</script>

<template>
  <div
    v-if="galleryItems?.length"
    :class="[
      `media-gallery`,
      `media-gallery--${galleryItems.length}`,
      `media-gallery--${galleryItems.length % 2 === 0 ? 'even' : 'odd'}`,
      {
        [`media-gallery--show-all`]: galleryOptions.showAll,
      },
    ]"
  >
    <div class="media-gallery__container">
      <UiMediaGalleryMain
        :name="props.name"
        class="media-gallery__main"
      >
        <template #gallery-items>
          <slot name="gallery-items" />
        </template>
      </UiMediaGalleryMain>

      <UiMediaGalleryNavigation
        :name="props.name"
        class="media-gallery__navigation"
      >
        <template #gallery-prev-icon>
          <slot name="gallery-prev-icon" />
        </template>

        <template #gallery-next-icon>
          <slot name="gallery-next-icon" />
        </template>
      </UiMediaGalleryNavigation>

      <UiMediaGalleryThumbs
        :name="props.name"
        class="media-gallery__thumbs"
        :thumbs-trigger="galleryOptions.thumbsTrigger"
      >
        <template #thumbs-prev-icon>
          <slot name="thumbs-prev-icon" />
        </template>

        <template #thumbs-next-icon>
          <slot name="thumbs-next-icon" />
        </template>
      </UiMediaGalleryThumbs>

      <UiMediaGalleryDots
        :name="props.name"
        class="media-gallery__dots"
      />

      <UiMediaGalleryXofY
        :name="props.name"
        class="media-gallery__x-of-y"
      >
        <template #divider>
          /
        </template>
      </UiMediaGalleryXofY>

      <slot name="gallery-controls" />
    </div>

    <UiMediaGalleryFullscreen :name="props.name" />
  </div>
</template>

<style>
@import url("~layers/components/Ui/MediaGallery/index.css");
</style>
